import { useState, useEffect } from 'react';
import { ArrowLeft, RefreshCw, Play, CheckCircle, AlertCircle, Clock, Database, Zap, Trash2 } from 'lucide-react';
import { supabase } from '../utils/supabaseStorage';
import { LoadingSpinner } from '../components/LoadingSpinner';

interface ProcessingAsset {
  id: string;
  hy_user_id: string;
  fal_request_id: string;
  fal_polling_node_name: string;
  filename: string;
  media_type: string;
  created_at: string;
  model: string;
  generation_prompt: string;
  status: string;
  public_url: string;
}

export const ProcessingMonitorPage = () => {
  const [processingAssets, setProcessingAssets] = useState<ProcessingAsset[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [processing, setProcessing] = useState<Set<string>>(new Set());
  const [deleting, setDeleting] = useState<Set<string>>(new Set());
  const [results, setResults] = useState<Record<string, { success: boolean; message: string }>>({});
  const [autoRefresh, setAutoRefresh] = useState(false);

  const fetchProcessingAssets = async () => {
    try {
      setLoading(true);
      setError(null);

      const { data, error: fetchError } = await supabase
        .rpc('processing_assets_check');

      if (fetchError) {
        throw fetchError;
      }

      setProcessingAssets(data || []);
      console.log('📊 [MONITOR] Loaded processing assets:', data?.length || 0);
    } catch (err) {
      console.error('Error fetching processing assets:', err);
      setError(err instanceof Error ? err.message : 'Failed to fetch processing assets');
    } finally {
      setLoading(false);
    }
  };

  const deleteAsset = async (asset: ProcessingAsset) => {
    const confirmed = window.confirm(
      `Are you sure you want to delete this asset?\n\n` +
      `File: ${asset.filename}\n` +
      `User: ${asset.hy_user_id.slice(0, 12)}...\n\n` +
      `This action cannot be undone.`
    );

    if (!confirmed) return;

    setDeleting(prev => new Set([...prev, asset.id]));

    try {
      console.log(`🗑️ [DELETE] Deleting asset ${asset.id}`);

      const { error: deleteError } = await supabase
        .from('assets')
        .delete()
        .eq('id', asset.id);

      if (deleteError) {
        throw deleteError;
      }

      console.log(`✅ [DELETE] Asset ${asset.id} deleted successfully`);
      
      setResults(prev => ({
        ...prev,
        [asset.id]: { success: true, message: 'Asset deleted successfully!' }
      }));

      // Refresh the list to remove the deleted asset
      await fetchProcessingAssets();

    } catch (error) {
      console.error(`💥 [DELETE] Error deleting asset ${asset.id}:`, error);
      setResults(prev => ({
        ...prev,
        [asset.id]: { success: false, message: error instanceof Error ? error.message : 'Failed to delete asset' }
      }));
    } finally {
      setDeleting(prev => {
        const newSet = new Set(prev);
        newSet.delete(asset.id);
        return newSet;
      });
    }
  };

  const processIndividualAsset = async (asset: ProcessingAsset) => {
    if (!asset.fal_request_id || !asset.fal_polling_node_name) {
      setResults(prev => ({
        ...prev,
        [asset.id]: { success: false, message: 'Missing FAL request ID or polling node name' }
      }));
      return;
    }

    setProcessing(prev => new Set([...prev, asset.id]));
    
    try {
      console.log(`🔄 [PROCESS] Processing asset ${asset.id}`);
      console.log(`📋 [PROCESS] Request ID: ${asset.fal_request_id}`);
      console.log(`📋 [PROCESS] Node Name: ${asset.fal_polling_node_name}`);

      // Step 1: Check FAL status
      const statusResult = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/fal-proxy`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          action: 'status',
          endpoint: asset.fal_polling_node_name,
          requestId: asset.fal_request_id
        }),
      });

      const statusData = await statusResult.json();
      console.log(`📊 [STATUS] Asset ${asset.id} status:`, statusData);

      if (!statusResult.ok) {
        throw new Error(`Status check failed: ${statusData.error}`);
      }

      if (statusData.status === 'COMPLETED' || statusData.status === 'completed') {
        console.log(`✅ [STATUS] Asset ${asset.id} is complete, fetching result...`);

        // Step 2: Get the result
        const resultResponse = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/fal-proxy`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            action: 'result',
            endpoint: asset.fal_polling_node_name,
            requestId: asset.fal_request_id
          }),
        });

        const resultData = await resultResponse.json();
        console.log(`📦 [RESULT] Asset ${asset.id} result:`, resultData);

        if (!resultResponse.ok) {
          throw new Error(`Result fetch failed: ${resultData.error}`);
        }

        // Step 3: Extract and upload media
        const mediaResult = await extractAndUploadMedia(resultData, asset);
        
        if (mediaResult.success) {
          // Step 4: Update asset in database
          const { error: updateError } = await supabase
            .from('assets')
            .update({
              public_url: mediaResult.publicUrl,
              status: 'completed',
              filename: mediaResult.filename || asset.filename,
              file_size: mediaResult.fileSize,
              updated_at: new Date().toISOString()
            })
            .eq('id', asset.id);

          if (updateError) {
            throw new Error(`Database update failed: ${updateError.message}`);
          }

          setResults(prev => ({
            ...prev,
            [asset.id]: { success: true, message: 'Asset completed and saved successfully!' }
          }));

          // Refresh the list
          await fetchProcessingAssets();
        } else {
          throw new Error(mediaResult.error || 'Failed to process media');
        }

      } else if (statusData.status === 'FAILED' || statusData.status === 'failed') {
        console.log(`❌ [STATUS] Asset ${asset.id} failed`);
        
        // Mark as failed
        await supabase
          .from('assets')
          .update({
            status: 'failed',
            updated_at: new Date().toISOString()
          })
          .eq('id', asset.id);

        setResults(prev => ({
          ...prev,
          [asset.id]: { success: false, message: 'Generation failed at FAL API level' }
        }));

        await fetchProcessingAssets();
      } else {
        setResults(prev => ({
          ...prev,
          [asset.id]: { success: false, message: `Still processing (Status: ${statusData.status})` }
        }));
      }

    } catch (error) {
      console.error(`💥 [PROCESS] Error processing asset ${asset.id}:`, error);
      setResults(prev => ({
        ...prev,
        [asset.id]: { success: false, message: error instanceof Error ? error.message : 'Unknown error' }
      }));
    } finally {
      setProcessing(prev => {
        const newSet = new Set(prev);
        newSet.delete(asset.id);
        return newSet;
      });
    }
  };

  const processAllAssets = async () => {
    const readyAssets = processingAssets.filter(asset => 
      asset.fal_request_id && asset.fal_polling_node_name
    );

    console.log(`🚀 [BATCH] Processing ${readyAssets.length} ready assets`);

    for (const asset of readyAssets) {
      await processIndividualAsset(asset);
      // Small delay between requests to avoid rate limiting
      await new Promise(resolve => setTimeout(resolve, 1000));
    }
  };

  const extractAndUploadMedia = async (resultData: any, asset: ProcessingAsset) => {
    try {
      // Extract media URL from FAL result
      let mediaUrl: string | null = null;
      let contentType: string | undefined;
      let fileName: string | undefined;
      let fileSize: number | undefined;

      // Try different extraction patterns
      if (resultData.images && Array.isArray(resultData.images) && resultData.images.length > 0) {
        const image = resultData.images[0];
        mediaUrl = image.url || image;
        contentType = image.content_type;
        fileName = image.file_name;
        fileSize = image.file_size;
      } else if (resultData.image) {
        const image = resultData.image;
        mediaUrl = image.url || image;
        contentType = image.content_type;
        fileName = image.file_name;
        fileSize = image.file_size;
      } else if (resultData.video) {
        const video = resultData.video;
        mediaUrl = video.url || video;
        contentType = video.content_type;
        fileName = video.file_name;
        fileSize = video.file_size;
      } else if (resultData.audio) {
        const audio = resultData.audio;
        mediaUrl = audio.url || audio;
        contentType = audio.content_type;
        fileName = audio.file_name;
        fileSize = audio.file_size;
      } else {
        // Search for any URL in the result
        for (const [_key, value] of Object.entries(resultData)) {
          if (typeof value === 'string' && value.includes('http') && 
              (value.includes('.jpg') || value.includes('.png') || value.includes('.mp4') || value.includes('.mp3'))) {
            mediaUrl = value;
            break;
          }
        }
      }

      if (!mediaUrl) {
        return {
          success: false,
          error: `No media URL found in FAL result. Available keys: ${Object.keys(resultData).join(', ')}`
        };
      }

      console.log(`📁 [UPLOAD] Found media URL: ${mediaUrl}`);

      // Download media from FAL
      const mediaResponse = await fetch(mediaUrl);
      if (!mediaResponse.ok) {
        throw new Error(`Failed to download media: ${mediaResponse.status}`);
      }

      const mediaBlob = await mediaResponse.blob();
      const fileExt = fileName?.split('.').pop()?.toLowerCase() || 
                     (asset.media_type === 'video' ? 'mp4' : asset.media_type === 'audio' ? 'mp3' : 'jpg');
      
      // Create storage path
      const timestamp = Date.now();
      const randomId = Math.random().toString(36).substr(2, 9);
      const storagePath = `${asset.hy_user_id}/${timestamp}-${randomId}.${fileExt}`;

      // Upload to Supabase storage
      const { data: _uploadData, error: uploadError } = await supabase.storage
        .from('generated')
        .upload(storagePath, mediaBlob, {
          cacheControl: '3600',
          upsert: false,
          contentType: contentType || mediaResponse.headers.get('content-type') || undefined
        });

      if (uploadError) {
        throw new Error(`Storage upload failed: ${uploadError.message}`);
      }

      // Get public URL
      const { data: { publicUrl } } = supabase.storage
        .from('generated')
        .getPublicUrl(storagePath);

      console.log(`✅ [UPLOAD] Successfully uploaded to: ${publicUrl}`);

      return {
        success: true,
        publicUrl,
        filename: fileName || asset.filename,
        fileSize: fileSize || mediaBlob.size
      };

    } catch (error) {
      console.error('💥 [UPLOAD] Media processing error:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error'
      };
    }
  };

  // Auto-refresh functionality
  useEffect(() => {
    let interval: NodeJS.Timeout;
    
    if (autoRefresh) {
      interval = setInterval(() => {
        fetchProcessingAssets();
      }, 30000); // Refresh every 30 seconds
    }

    return () => {
      if (interval) {
        clearInterval(interval);
      }
    };
  }, [autoRefresh]);

  // Initial load
  useEffect(() => {
    fetchProcessingAssets();
  }, []);

  const goBack = () => {
    window.history.back();
  };

  const getStatusIcon = (asset: ProcessingAsset) => {
    const result = results[asset.id];
    const isProcessing = processing.has(asset.id);
    const isDeleting = deleting.has(asset.id);
    
    if (isProcessing || isDeleting) return <LoadingSpinner size="sm" />;
    if (result?.success) return <CheckCircle className="w-4 h-4 text-status-success" />;
    if (result && !result.success) return <AlertCircle className="w-4 h-4 text-status-error" />;
    return <Clock className="w-4 h-4 text-status-warning" />;
  };

  const getStatusMessage = (asset: ProcessingAsset) => {
    const result = results[asset.id];
    const isProcessing = processing.has(asset.id);
    const isDeleting = deleting.has(asset.id);
    
    if (isProcessing) return 'Processing...';
    if (isDeleting) return 'Deleting...';
    if (result?.success) return result.message;
    if (result && !result.success) return result.message;
    return 'Ready to process';
  };

  const hasValidData = (asset: ProcessingAsset) => {
    return asset.fal_request_id && asset.fal_polling_node_name;
  };

  return (
    <div className="min-h-screen">
      {/* Header */}
      <header className="border-b border-subtle bg-surface sticky top-0 z-30">
        <div className="px-4 py-4">
          <div className="flex items-center justify-between max-w-6xl mx-auto">
            <div className="flex items-center space-x-3">
              <button
                onClick={goBack}
                className="p-2 text-th-secondary hover:text-th-primary transition-colors rounded-lg hover:bg-overlay"
              >
                <ArrowLeft className="w-5 h-5" />
              </button>
              <div className="flex items-center space-x-2">
                <div className="w-8 h-8 bg-brand rounded-lg flex items-center justify-center">
                  <Database className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h1 className="text-lg font-bold text-th-primary">Processing Assets Monitor</h1>
                  <p className="text-xs text-th-secondary">Monitor and manually process stuck assets</p>
                </div>
              </div>
            </div>

            <div className="flex items-center space-x-4">
              {/* Auto-refresh toggle */}
              <label className="flex items-center space-x-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={autoRefresh}
                  onChange={(e) => setAutoRefresh(e.target.checked)}
                  className="w-4 h-4 text-brand bg-elevated border-subtle rounded focus:ring-brand focus:ring-2"
                />
                <span className="text-th-secondary text-sm">Auto-refresh</span>
              </label>

              <button
                onClick={fetchProcessingAssets}
                disabled={loading}
                className="flex items-center space-x-2 px-4 py-2 bg-accent text-surface rounded-lg font-medium hover:bg-accent/90 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
              >
                <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
                <span>Refresh</span>
              </button>

              <button
                onClick={processAllAssets}
                disabled={processing.size > 0 || processingAssets.length === 0}
                className="flex items-center space-x-2 px-4 py-2 bg-brand text-surface rounded-lg font-medium hover:bg-brand-hover disabled:opacity-50 disabled:cursor-not-allowed transition-all"
              >
                <Zap className="w-4 h-4" />
                <span>Process All</span>
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-6xl mx-auto px-4 py-8">
        {/* Summary Stats */}
        <div className="grid grid-cols-1 sm:grid-cols-4 gap-4 mb-8">
          <div className="card p-4">
            <div className="flex items-center space-x-3">
              <div className="p-2 bg-status-warning/20 rounded-lg">
                <Clock className="w-5 h-5 text-status-warning" />
              </div>
              <div>
                <p className="text-th-secondary text-sm">Processing</p>
                <p className="text-2xl font-bold text-status-warning">{processingAssets.length}</p>
              </div>
            </div>
          </div>

          <div className="card p-4">
            <div className="flex items-center space-x-3">
              <div className="p-2 bg-status-success/20 rounded-lg">
                <CheckCircle className="w-5 h-5 text-status-success" />
              </div>
              <div>
                <p className="text-th-secondary text-sm">Completed</p>
                <p className="text-2xl font-bold text-status-success">
                  {Object.values(results).filter(r => r.success).length}
                </p>
              </div>
            </div>
          </div>

          <div className="card p-4">
            <div className="flex items-center space-x-3">
              <div className="p-2 bg-status-error/20 rounded-lg">
                <AlertCircle className="w-5 h-5 text-status-error" />
              </div>
              <div>
                <p className="text-th-secondary text-sm">Failed</p>
                <p className="text-2xl font-bold text-status-error">
                  {Object.values(results).filter(r => !r.success).length}
                </p>
              </div>
            </div>
          </div>

          <div className="card p-4">
            <div className="flex items-center space-x-3">
              <div className="p-2 bg-brand/20 rounded-lg">
                <Database className="w-5 h-5 text-brand" />
              </div>
              <div>
                <p className="text-th-secondary text-sm">Ready</p>
                <p className="text-2xl font-bold text-brand">
                  {processingAssets.filter(hasValidData).length}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Processing Assets Table */}
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="text-center space-y-4">
              <LoadingSpinner size="lg" />
              <p className="text-th-secondary">Loading processing assets...</p>
            </div>
          </div>
        ) : error ? (
          <div className="flex items-center justify-center py-12">
            <div className="text-center space-y-4 max-w-md">
              <AlertCircle className="w-16 h-16 text-status-error mx-auto" />
              <h3 className="text-xl font-bold text-th-primary">Failed to Load</h3>
              <p className="text-th-secondary">{error}</p>
              <button onClick={fetchProcessingAssets} className="button-primary">
                Try Again
              </button>
            </div>
          </div>
        ) : processingAssets.length === 0 ? (
          <div className="text-center py-12">
            <div className="space-y-6">
              <div className="w-24 h-24 bg-brand rounded-full flex items-center justify-center mx-auto">
                <CheckCircle className="w-12 h-12 text-white" />
              </div>
              <div>
                <h3 className="text-xl font-bold text-th-primary mb-2">No Processing Assets</h3>
                <p className="text-th-secondary">
                  All background generations are complete or there are no assets in processing status.
                </p>
              </div>
            </div>
          </div>
        ) : (
          <div className="card p-6">
            <h3 className="text-lg font-semibold text-th-primary mb-6">
              Processing Assets ({processingAssets.length})
            </h3>

            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-surface border-b border-subtle">
                  <tr className="text-left">
                    <th className="px-4 py-3 text-xs font-medium text-th-secondary uppercase tracking-wider">Asset</th>
                    <th className="px-4 py-3 text-xs font-medium text-th-secondary uppercase tracking-wider">User</th>
                    <th className="px-4 py-3 text-xs font-medium text-th-secondary uppercase tracking-wider">FAL Request</th>
                    <th className="px-4 py-3 text-xs font-medium text-th-secondary uppercase tracking-wider">Status</th>
                    <th className="px-4 py-3 text-xs font-medium text-th-secondary uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-hyperBorder">
                  {processingAssets.map((asset) => (
                    <tr key={asset.id} className="hover:bg-overlay transition-colors">
                      <td className="px-4 py-4">
                        <div className="flex items-center space-x-3">
                          <div className="w-8 h-8 bg-brand rounded-lg flex items-center justify-center">
                            <span className="text-white text-xs font-bold">
                              {asset.media_type === 'image' ? '🖼️' : asset.media_type === 'video' ? '🎬' : '🎵'}
                            </span>
                          </div>
                          <div>
                            <p className="text-th-primary font-medium text-sm">
                              {asset.filename || asset.id.slice(0, 8)}
                            </p>
                            <p className="text-th-secondary text-xs">
                              {asset.media_type} • {new Date(asset.created_at).toLocaleString()}
                            </p>
                          </div>
                        </div>
                      </td>

                      <td className="px-4 py-4">
                        <div className="text-th-primary font-mono text-xs">
                          {asset.hy_user_id.slice(0, 12)}...
                        </div>
                      </td>

                      <td className="px-4 py-4">
                        <div className="space-y-1">
                          <div className="flex items-center space-x-2">
                            <span className="text-th-secondary text-xs">Request ID:</span>
                            <code className="text-brand font-mono text-xs bg-brand/10 px-1 rounded">
                              {asset.fal_request_id ? asset.fal_request_id.slice(0, 8) + '...' : 'Missing'}
                            </code>
                          </div>
                          <div className="flex items-center space-x-2">
                            <span className="text-th-secondary text-xs">Node:</span>
                            <code className="text-accent font-mono text-xs bg-accent/10 px-1 rounded">
                              {asset.fal_polling_node_name || 'Missing'}
                            </code>
                          </div>
                        </div>
                      </td>

                      <td className="px-4 py-4">
                        <div className="flex items-center space-x-2">
                          {getStatusIcon(asset)}
                          <span className={`text-sm ${
                            results[asset.id]?.success ? 'text-status-success' :
                            results[asset.id] && !results[asset.id].success ? 'text-status-error' :
                            'text-th-secondary'
                          }`}>
                            {getStatusMessage(asset)}
                          </span>
                        </div>
                        {!hasValidData(asset) && (
                          <p className="text-status-error text-xs mt-1">Missing required data</p>
                        )}
                      </td>

                      <td className="px-4 py-4">
                        <div className="flex items-center space-x-2">
                          {/* Process Button */}
                          <button
                            onClick={() => processIndividualAsset(asset)}
                            disabled={processing.has(asset.id) || deleting.has(asset.id) || !hasValidData(asset)}
                            className="flex items-center space-x-1 px-3 py-1.5 bg-brand text-surface rounded-lg text-xs font-medium hover:bg-brand-hover disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                          >
                            <Play className="w-3 h-3" />
                            <span>Process</span>
                          </button>

                          {/* Delete Button */}
                          <button
                            onClick={() => deleteAsset(asset)}
                            disabled={processing.has(asset.id) || deleting.has(asset.id)}
                            className="flex items-center space-x-1 px-3 py-1.5 bg-status-error text-white rounded-lg text-xs font-medium hover:bg-status-error/90 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                            title="Delete asset from database"
                          >
                            {deleting.has(asset.id) ? (
                              <LoadingSpinner size="sm" />
                            ) : (
                              <Trash2 className="w-3 h-3" />
                            )}
                            <span>Delete</span>
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Debug Info */}
        {processingAssets.length > 0 && (
          <div className="mt-8 card p-4">
            <h4 className="text-th-primary font-medium mb-3">Debug Information</h4>
            <div className="text-sm text-th-secondary space-y-1">
              <p>• Total processing assets: {processingAssets.length}</p>
              <p>• Assets with valid FAL data: {processingAssets.filter(hasValidData).length}</p>
              <p>• Assets missing request ID: {processingAssets.filter(a => !a.fal_request_id).length}</p>
              <p>• Assets missing polling node: {processingAssets.filter(a => !a.fal_polling_node_name).length}</p>
              <p>• Auto-refresh: {autoRefresh ? 'Enabled (30s)' : 'Disabled'}</p>
            </div>
          </div>
        )}
      </main>

      {/* Background Effects */}
      <div className="fixed inset-0 -z-10 overflow-hidden pointer-events-none">
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-status-warning/5 rounded-full blur-3xl" />
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-status-warning/10 rounded-full blur-3xl" />
      </div>
    </div>
  );
};